<script setup lang="ts">
import { TrendingUp, Tag, Calendar, ArrowRight } from 'lucide-vue-next';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

const props = defineProps<{
    totalValuation: number;
    totalPlusValue: number;
    totalInvested: number;
    filteredCount: number;
    loading: boolean;
    isAmountsHidden: boolean;
    formatAmount: (val: any) => string;
    categoriesTree: any[];
    pastYears: number;
    futureYears: number;
}>();

const selectedCategory = defineModel<number | null>('selectedCategory', { default: null });
const emit = defineEmits(['update:pastYears', 'update:futureYears']);

const onPastYearsChange = (e: any) => { emit('update:pastYears', parseInt(e.target.value)) };
const onFutureYearsChange = (e: any) => { emit('update:futureYears', parseInt(e.target.value)) };
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <!-- Global Total -->
        <Card class="border-border shadow-sm bg-zinc-900 dark:bg-zinc-950 text-white overflow-hidden relative">
            <div class="absolute right-[-10px] top-[-10px] opacity-10">
                <TrendingUp class="w-32 h-32" />
            </div>
            <CardHeader class="pb-2">
                <CardDescription class="text-zinc-400 font-bold uppercase tracking-widest text-[10px]">Patrimoine Net Total</CardDescription>
                <CardTitle v-if="!loading" class="text-3xl font-black tabular-nums" :class="{ 'blur-md select-none': isAmountsHidden }">
                    {{ formatAmount(totalValuation) }}€
                </CardTitle>
                <Skeleton v-else class="h-9 w-32 bg-zinc-800" />
            </CardHeader>
            <CardContent>
                <div class="flex items-center gap-1.5 text-xs text-emerald-400 font-bold">
                    <span v-if="!loading" class="bg-emerald-500/20 px-2 py-0.5 rounded">Calculé sur {{ filteredCount }} actifs</span>
                    <Skeleton v-else class="h-4 w-24 bg-zinc-800" />
                </div>
            </CardContent>
        </Card>

        <!-- Plus-value -->
        <Card class="border-border shadow-sm">
            <CardHeader class="pb-2">
                <CardDescription class="text-zinc-500 font-bold uppercase tracking-widest text-[10px]">Plus-value Latente</CardDescription>
                <CardTitle v-if="!loading" class="text-3xl font-black tabular-nums" :class="[totalPlusValue >= 0 ? 'text-emerald-600' : 'text-red-600', { 'blur-md select-none': isAmountsHidden }]">
                    {{ totalPlusValue >= 0 ? '+' : '' }}{{ formatAmount(Math.abs(totalPlusValue)) }}€
                </CardTitle>
                <Skeleton v-else class="h-9 w-32" />
            </CardHeader>
            <CardContent>
                <div class="flex items-center gap-2">
                    <Badge v-if="!loading && totalInvested > 0" variant="outline" class="bg-zinc-50 text-zinc-600 border-zinc-200 text-[10px] font-bold">
                        Investi: {{ formatAmount(totalInvested) }}€
                    </Badge>
                    <Skeleton v-else-if="loading" class="h-4 w-24" />
                </div>
            </CardContent>
        </Card>

        <!-- Category Filter Card -->
        <Card class="border-border shadow-sm lg:col-span-1">
            <CardHeader class="pb-2">
                 <CardDescription class="text-zinc-500 font-bold uppercase tracking-widest text-[10px] flex items-center gap-1">
                     <Tag class="w-3 h-3" /> Filtrer par catégorie
                 </CardDescription>
            </CardHeader>
            <CardContent>
                 <select v-if="categoriesTree.length > 0" v-model="selectedCategory" 
                         class="w-full bg-muted border border-border rounded-lg p-2 text-sm font-bold text-foreground focus:ring-2 focus:ring-primary/10 transition-all outline-none focus-visible:ring-indigo-500"
                         aria-label="Filtrer par catégorie">
                    <option :value="null">Toutes les catégories</option>
                    <option v-for="cat in categoriesTree" :key="cat.id" :value="cat.id">
                        {{ cat.displayName }}
                    </option>
                 </select>
                 <Skeleton v-else class="h-9 w-full rounded-lg" aria-hidden="true" />
            </CardContent>
        </Card>

        <!-- Time Range Card -->
        <Card class="border-border shadow-sm">
            <CardHeader class="pb-2">
                <CardDescription class="text-zinc-500 font-bold uppercase tracking-widest text-[10px] flex items-center gap-1">
                    <Calendar class="w-3 h-3" /> Fenêtre temporelle
                </CardDescription>
            </CardHeader>
            <CardContent class="p-4 pt-0">
                 <div class="flex items-center gap-2">
                    <div class="flex-1 space-y-1">
                        <label class="text-[9px] uppercase font-bold text-zinc-400 block">Passé</label>
                        <select :value="pastYears" @change="onPastYearsChange" 
                                class="w-full bg-muted border border-border rounded p-1.5 text-xs font-bold text-foreground focus-visible:ring-indigo-500 outline-none">
                            <option :value="1">1 an</option>
                            <option :value="3">3 ans</option>
                            <option :value="5">5 ans</option>
                            <option :value="10">10 ans</option>
                            <option :value="50">Max</option>
                        </select>
                    </div>
                    <ArrowRight class="w-4 h-4 text-muted mt-4" aria-hidden="true" />
                    <div class="flex-1 space-y-1">
                        <label class="text-[9px] uppercase font-bold text-muted-foreground block">Futur</label>
                        <select :value="futureYears" @change="onFutureYearsChange" 
                                class="w-full bg-muted border border-border rounded p-1.5 text-xs font-bold text-foreground focus-visible:ring-indigo-500 outline-none">
                            <option :value="1">1 an</option>
                            <option :value="5">5 ans</option>
                            <option :value="10">10 ans</option>
                            <option :value="20">20 ans</option>
                            <option :value="50">Max</option>
                        </select>
                    </div>
                 </div>
            </CardContent>
        </Card>
    </div>
</template>
